import type { EndingResult, EndingType, GameFacts, GroundCause, StageCurve } from '@chess/api-model';
import { GROUND_CAUSES } from '@chess/api-model';
import { moveCause, moveCost, patternOf } from '@chess/api-mistakes';

// The stage-specific third card's data (2026-10-08), one shape per stage,
// each read from the game's facts: the opening's variation; the
// middlegame's win chance lost to each cause (the "How you lost ground"
// causes, `moveCause` over the game's counted middlegame mistakes, their
// patterns from the classifier; no clocks here, so Time comes only from the
// game's own `groundCause`); the ending's type, length and case.

export interface MiddlegameDetail {
  /** Win chance lost to each cause over the middlegame, in percentage points. */
  costs: Record<GroundCause, number>;
  /** The game's cause when its middlegame went wrong (`GameFacts.groundCause`) and the move to practise. */
  cause?: GroundCause;
  practice?: { ply: number; san: string };
}

export interface EndingDetail {
  type?: EndingType;
  /** The ending's length in the reader's moves, from its curve. */
  moves: number;
  endingCase?: GameFacts['endingCase'];
  result?: EndingResult;
}

type MiddlegameFacts = Pick<GameFacts, 'colour' | 'mistakes' | 'groundCause' | 'groundPractice'>;
type EndingFacts = Pick<GameFacts, 'endingType' | 'endingCase' | 'endingResult'>;

/** The middlegame's mistakes by cause, each cause's cost in win chance. */
export function middlegameDetail(facts: MiddlegameFacts): MiddlegameDetail {
  const costs = Object.fromEntries(GROUND_CAUSES.map((c) => [c, 0])) as Record<GroundCause, number>;
  for (const m of facts.mistakes ?? []) {
    if (m.stage !== 'middlegame') continue;
    const cause = moveCause({ kind: m.kind, pattern: patternOf(m, facts.colour).pattern });
    costs[cause] += moveCost(m);
  }
  return { costs, cause: facts.groundCause, practice: facts.groundPractice };
}

/** The ending's type, length and case. */
export function endingDetail(facts: EndingFacts, curve: StageCurve): EndingDetail {
  return { type: facts.endingType, moves: Math.ceil((curve.toPly - curve.fromPly) / 2), endingCase: facts.endingCase, result: facts.endingResult };
}

/** The period's record in one kind of ending: games by result. */
export type TypeRecord = Partial<Record<EndingResult, number>>;
